import { useState } from 'react'

import styled from '@emotion/styled'
import { useInView } from 'react-cool-inview'

import { ArcadeButton, spacing, Stack } from '@cash-design-system/react'
import { DynamicDisclosureLayout } from '@cash-web/dynamic-disclosure'
import { DisclosureBlocker } from '@cash-web/protos/squareup/franklin/blockers/disclosure_blocker.pb'
import { useMutationConfirmDisclosure } from '@cash-web/shared-data-access-api/react-query'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { BlockerProps } from '../..'

interface Props extends BlockerProps {
  blocker: DisclosureBlocker
}

const DisclosureModalContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  position: relative;
  max-width: ${({ theme }) => theme.maxWidths.content};
`

const StickyFooter = styled.div<{ isInView?: boolean }>`
  bottom: calc(-1 * ${spacing.defaultContent});
  background: ${props => props.theme.arcadeColors.component.modalBackground};
  padding-bottom: ${spacing.m};
  padding-top: ${spacing.m};
  position: sticky;

  border-top: ${props => props.theme.border.width.small} solid
    ${props => props.theme.arcadeColors.semantic.borderDivider};
`

const ObservationPixel = styled.div`
  bottom: 0;
  height: ${props => props.theme.pxToRem(1)};
  position: absolute;
  transform: translateY(-${spacing.m});
  width: 100%;
`

export const BlockerDisclosure = ({ blocker }: Props) => {
  const flow = useFlowContext()
  const { mutateAsync: confirmDisclosure, isLoading } = useMutationConfirmDisclosure()

  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const { observe, inView } = useInView({ threshold: 1, onEnter: () => setHasScrolledToBottom(true) })
  const disableConfirmButton = !hasScrolledToBottom

  const handleSubmit = async () => {
    flow.updateWithResponse({
      response: confirmDisclosure({
        request_context: {},
      }),
      queriesToInvalidate: [],
    })
  }

  return (
    <DisclosureModalContentContainer>
      <Stack space={spacing.xl}>
        <DynamicDisclosureLayout disclosure={blocker} />

        <StickyFooter isInView={inView}>
          <ArcadeButton
            size="cta"
            prominence="prominent"
            disabled={disableConfirmButton}
            isLoading={isLoading}
            onClick={handleSubmit}
          >
            {disableConfirmButton ? blocker?.scroll_button_text : blocker?.accept_button_text}
          </ArcadeButton>
        </StickyFooter>
      </Stack>
      <ObservationPixel ref={observe} data-testid="scroll-to" />
    </DisclosureModalContentContainer>
  )
}
